import { compact } from "lodash";
import { ReactNode, useState } from "react";
import { ArtifactType, GetProfileQueryQuery } from "types/graphql-schema";

import ArtifactCreationDialog from "@apps/artifact-creation-dialog/artifact-creation-dialog";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Dropdown from "@components/dropdown/dropdown";
import { DropdownOptionType } from "@components/dropdown/dropdown-types";
import { ToggleButtonGroupType } from "@components/toggle-button-group/toggle-button-group";
import { getLocalStorage, setLocalStorage } from "@helpers/helpers";

import ProfileResponsiveTabs from "../../profile-responsive-tabs";
import getProfileArtifactsQuery from "../graphql/get-profile-artifacts-query";
import {
  ProfileActionItems,
  ProfileFeedback,
  ProfileGoals,
  ProfileRecognitions,
} from "./profile-artifacts";

type ProfileForItems = NonNullable<GetProfileQueryQuery["user"]>;

// Remember the last-viewed sub-tab across reloads/sessions. Kept in
// localStorage (not the backend UI-preference cache) since it's a purely
// local, low-stakes preference. Global rather than per-profile: the user's
// preferred item type carries across whichever profile they open next.
const SELECTED_TAB_STORAGE_KEY = "profileItemsSelectedTab";

// Prefill the creation dialog so the new item targets this profile, matching
// the per-tab create buttons the "Items" tab replaces.
const getCreateFormOptions = (
  artifactType: ArtifactType,
  profile: ProfileForItems,
) => {
  switch (artifactType) {
    case ArtifactType.Goal:
      return { artifactType, owners: [profile] };
    case ArtifactType.Recognition:
      return { artifactType, recognitionRecipients: [profile] };
    case ArtifactType.Feedback:
      return { artifactType, feedbackRecipients: [profile] };
    case ArtifactType.ActionItem:
    default:
      return { artifactType, assignee: profile };
  }
};

// The combined "Items" tab: one header + a "Create" dropdown over the four
// artifact types, with sub-tabs that swap between the same lists previously
// shown on their own profile tabs (goals, recognitions, feedback, action
// items).
const ProfileItems = ({ profile }: { profile: ProfileForItems }) => {
  const label = useLabel();
  const organization = currentOrganizationVar();
  const [createArtifactType, setCreateArtifactType] =
    useState<ArtifactType | null>(null);

  const itemTypes = compact<{
    value: string;
    label: string;
    render: () => ReactNode;
  }>([
    organization.featureFlags.goals && {
      value: "goals",
      label: label("goal", { titleCase: true, pluralize: true }),
      render: () => <ProfileGoals profile={profile} bare hideHeader />,
    },
    organization.featureFlags.actionItems && {
      value: "action-items",
      label: label("action item", { titleCase: true, pluralize: true }),
      render: () => <ProfileActionItems profile={profile} bare hideHeader />,
    },
    organization.featureFlags.recognitions && {
      value: "recognitions",
      label: label("recognition", { titleCase: true, pluralize: true }),
      render: () => <ProfileRecognitions profile={profile} bare hideHeader />,
    },
    organization.featureFlags.feedbacks && {
      value: "feedback",
      label: label("feedback", { titleCase: true, pluralize: true }),
      render: () => <ProfileFeedback profile={profile} bare hideHeader />,
    },
  ]);

  const [selectedValue, setSelectedValue] = useState<string | undefined>(() => {
    // Fall back to the first tab if the stored value is missing or points at a
    // type that's no longer available (e.g. a feature flag was turned off).
    const stored = getLocalStorage<string | null>(
      SELECTED_TAB_STORAGE_KEY,
      null,
    );
    return itemTypes.some((t) => t.value === stored)
      ? (stored ?? undefined)
      : itemTypes[0]?.value;
  });
  const selectedItemType =
    itemTypes.find((t) => t.value === selectedValue) ?? itemTypes[0];

  const handleSelectTab = (value: string | undefined) => {
    setSelectedValue(value);
    if (value) setLocalStorage(SELECTED_TAB_STORAGE_KEY, value);
  };

  const tabButtons: ToggleButtonGroupType<string>[] = itemTypes.map((t) => ({
    value: t.value,
    title: t.label,
    active: t.value === selectedItemType?.value,
  }));

  const createOptions = compact([
    organization.featureFlags.goals && {
      label: label("goal", { titleCase: true }),
      onClick: () => setCreateArtifactType(ArtifactType.Goal),
    },
    organization.featureFlags.actionItems && {
      label: label("action item", { titleCase: true }),
      onClick: () => setCreateArtifactType(ArtifactType.ActionItem),
    },
    organization.featureFlags.recognitions && {
      label: label("recognition", { titleCase: true }),
      onClick: () => setCreateArtifactType(ArtifactType.Recognition),
    },
    organization.featureFlags.feedbacks && {
      label: label("feedback", { titleCase: true }),
      onClick: () => setCreateArtifactType(ArtifactType.Feedback),
    },
  ]) as DropdownOptionType[];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between pt-2">
          <div className="text-lg font-semibold text-gray-900">Items</div>
          {createOptions.length > 0 && (
            <Dropdown options={createOptions} align="end" gap={8}>
              <Button
                theme={buttonTheme.primary}
                size={ButtonSize.medium}
                text="Create Item"
              />
            </Dropdown>
          )}
        </div>
        {tabButtons.length > 1 && (
          <ProfileResponsiveTabs
            buttons={tabButtons}
            onClick={(button) => handleSelectTab(button.value)}
          />
        )}
      </div>
      {selectedItemType?.render()}
      {createArtifactType && (
        <ArtifactCreationDialog
          hideTypeSelector
          formOptions={getCreateFormOptions(createArtifactType, profile)}
          onClose={() => setCreateArtifactType(null)}
          refetchQueries={[getProfileArtifactsQuery]}
        />
      )}
    </div>
  );
};

export default ProfileItems;
